import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Fade,
  GridLegacy as Grid,
  IconButton,
  Modal,
  Typography,
} from '@mui/material';
import { PropsWithChildren, useState } from 'react';
import { silobagDataDetailService } from '@old/services/domain/base/silobag/base/silobag-data-detail';

import CrossIcon from '@old/assets/svg/core/cross.svg';

import i18n from '../../../../libs/language';
import dayjs from 'dayjs';
import UTC from 'dayjs/plugin/utc';
dayjs.extend(UTC);

type SummarySilobagDetail = Awaited<ReturnType<typeof silobagDataDetailService>>['summary'];
type SatViewExtractionEvent = SummarySilobagDetail['silobags_history'][0]['sat_view_events'];

type Props = {
  open: boolean;
  onClose: () => void;
  target: SatViewExtractionEvent;
};

export function ExtractionSatViewModal({ open, onClose, target }: Props) {
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  const localePre = i18n.t('app.silobag_history.extraction_confirm.pre');
  const localePost = i18n.t('app.silobag_history.extraction_confirm.post');

  const images = [
    { data: target?.sat_view_id_1_image, label: localePre },
    { data: target?.sat_view_id_2_image, label: localePost },
  ];

  const CHIP_BG_COLOR: Record<string, string> = {
    [localePre]: '#FFF176',
    [localePost]: '#6baa00',
  };
  const CHIP_FONT_COLOR: Record<string, string> = {
    [localePre]: 'black',
    [localePost]: 'common.white',
  };

  return (
    <>
      <MyModal open={open} onClose={onClose}>
        <Typography variant="h5" gutterBottom>
          {i18n.t('app.silobag_history.extraction_confirm.modal_title')}
        </Typography>
        <Grid container spacing={2}>
          {images.map(({ data, label }) => (
            <Grid item xs={12} sm={6} key={label}>
              <Card elevation={4} sx={{ borderRadius: 2 }}>
                <CardActionArea onClick={() => data?.image_url && setZoomImage(data.image_url)}>
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia component="img" height="200" image={data?.image_url} alt={label} />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        bgcolor: 'rgba(0,0,0,0.6)',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#fff' }}>
                        {dayjs.utc(data?.timestamp).format('HH:mm — DD/MM/YYYY')}
                      </Typography>
                    </Box>
                  </Box>
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Chip
                        label={label}
                        size="small"
                        sx={{
                          backgroundColor: CHIP_BG_COLOR[label],
                          color: CHIP_FONT_COLOR[label],
                        }}
                      />
                      <Typography variant="body2" fontSize={12} color="textSecondary">
                        {parseFloat(data?.size ?? '0').toFixed(2)} m
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button variant="outlined" onClick={onClose} sx={{ borderColor: '#6baa00', color: '#6baa00' }}>
            {i18n.t('app.close')}
          </Button>
        </Box>
      </MyModal>

      {/* Zoom Modal */}
      <Modal open={Boolean(zoomImage)} onClose={() => setZoomImage(null)} closeAfterTransition>
        <Fade in={Boolean(zoomImage)} timeout={200}>
          <Box
            onClick={() => setZoomImage(null)}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              bgcolor: 'rgba(0,0,0,0.85)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            {/* Icono de cierre */}
            <IconButton
              onClick={() => setZoomImage(null)}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                color: 'common.white',
                zIndex: 10,
              }}
            >
              <img src={CrossIcon} style={{ color: 'red', width: 16, height: 16 }} />
            </IconButton>
            {zoomImage ? (
              <Box
                component="img"
                src={zoomImage}
                alt="Zoom Sat View"
                sx={{
                  maxWidth: '50%',
                  maxHeight: '50%',
                  boxShadow: 12,
                  borderRadius: 1,
                }}
              />
            ) : null}
          </Box>
        </Fade>
      </Modal>
    </>
  );
}

type ModalProps = { open: boolean; onClose: () => void };
function MyModal({ open, onClose, children }: PropsWithChildren<ModalProps>) {
  const style = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: 800,
    bgcolor: 'background.paper',
    borderRadius: 4,
    boxShadow: 24,
    p: 3,
    maxHeight: '90vh',
    overflowY: 'auto',
  };

  const handleClose = () => {
    // if (reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Modal open={open} keepMounted onClose={handleClose}>
      <Fade in={open} timeout={200}>
        <Box sx={style}>{children}</Box>
      </Fade>
    </Modal>
  );
}
